import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { catchError, Observable } from "rxjs";
import { BaseService } from "../../_shared/services/base.service";
import { ContactDto } from "../../contacts/models/contact";

import { OdataList } from "../../_shared/models/odataList";
import { SelectOption } from "../../_shared/models/option";
import { AddressListFilterFields } from "../form-models/address-filter-form";
import { Address, AddressList } from "../models/address";
import { VatNumberResponse } from "../models/vatNumberResponse";

@Injectable({
  providedIn: "root",
})
export class AddressService extends BaseService<Address, AddressList> {
  constructor(
    protected override http: HttpClient,
    @Inject("BASE_URL") protected override baseUrl: string
  ) {
    super(http, baseUrl, "address");
  }

  getAllFiltered(filter: AddressListFilterFields): Observable<OdataList<AddressList>> {
    return this.getAllOdata("?" + this.serializeForQueryStringOdata(filter));
  }

  getAllWithJobOffer(): Observable<AddressList[]> {
    return this.getAll<AddressList>("/withJoboffer");
  }

  getAllSwimmingPools(): Observable<AddressList[]> {
    return this.getAll<AddressList>("/getAllSwimmingPools");
  }

  getAllAddressesAndPersons(includeId?: string): Observable<ContactDto[]> {
    if (includeId) {
      return this.getAll<ContactDto>("/addressesAndPersons?includeid=" + includeId);
    }
    return this.getAll<ContactDto>("/addressesAndPersons");
  }

  search(searchTerm: string) {
    return this.getAll<AddressList>("/search?searchVal=" + searchTerm);
  }

  getKboData(vatNumber: string): Observable<AddressList | null> {
    const enterpriseNumber = vatNumber.replaceAll(" ", "").replaceAll(".", "").replace(/^BE/i, "");
    return this.get<AddressList>(`/lookupKbo/${enterpriseNumber}`).pipe(
      catchError(this.handleError<AddressList | null>())
    );
  }

  getVatData(vatNumber: string): Observable<VatNumberResponse | null> {
    vatNumber = vatNumber.replaceAll(" ", "").replaceAll(".", "");
    return this.http
      .get<VatNumberResponse>("https://controleerbtwnummer.eu/api/validate/" + vatNumber + ".json")
      .pipe(catchError(this.handleError<VatNumberResponse | null>()));
  }

  isBelgianVat(vatNumber: string): boolean {
    const cleaned = vatNumber.replaceAll(" ", "").replaceAll(".", "");
    // Belgian if starts with BE or has no letter prefix (only digits)
    return /^BE/i.test(cleaned) || /^\d/.test(cleaned);
  }

  updateIBAN(addressId: number, iban: string) {
    return this.put("/" + addressId + "/IBAN", { Iban: iban });
  }

  archiveAddress(id: number) {
    return this.http.put(this.baseUrl + "address/toggleDisable/" + id, null);
  }

  verifyAddress(
    address: string,
    nr: string,
    bus: string,
    postcode: string
  ): Observable<AddressList> {
    address = address || "";
    nr = nr || "";
    bus = bus || "";
    postcode = postcode || "";
    return this.get<any>(`/verify?street=${address}&nr=${nr}&bus=&postcode=${postcode}`);
  }

  canSendInvoiceViaPeppol(id: number): Observable<boolean> {
    return this.get(`/${id}/cansendInvoiceViaPeppol`);
  }

  exists(name: string, exceptId: number = 0): Observable<boolean> {
    return this.get(`/exists/${exceptId}?name=${name}`);
  }

  public contactTypes: SelectOption[] = [
    { id: "1", name: "Belgische persoon / particulier" },
    { id: "2", name: "Belgische organisatie zonder btw-nummer" },
    { id: "3", name: "Belgische organisatie / bedrijf met btw-nummer" },
    { id: "4", name: "Persoon / particulier binnen de EU" },
    { id: "5", name: "Organisatie binnen de EU zonder btw-nummer" },
    { id: "6", name: "Organisatie / bedrijf binnen de EU met btw-nummer" },
    { id: "7", name: "Persoon / particulier buiten de EU" },
    { id: "8", name: "Organisatie / bedrijf buiten de EU" },
  ];

  // TODO: Can this be moved so the contactTypes objects include these properties?
  public companyTypes: string[] = ["2", "3", "5", "6", "8"];
  public personTypes: string[] = ["1", "4", "7"];
  public btwNeeded: string[] = ["3", "6"];
  public noBtw: string[] = ["2", "5"];
  public belgianCompanies: string[] = ["1,", "2", "3"];
}
